import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Check, Copy, Archive, Globe2, Lock, Plus, X as XIcon, Users2,
  Loader2, Search,
} from "lucide-react";
import { getAdminAuthHeaders } from "./AdminAuthContext";
import { translateAdminError } from "./adminErrors";
import { StatusBadge } from "./StatusBadge";
import { COUNTRY_NAMES_ES } from "./AdminLocationSelector";
import {
  BILLING_TYPES, DURATION_UNITS, RENEWAL_OPTIONS, LISTINGS_MODES, CURRENCIES,
} from "./planConstants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "../components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COUNTRY_CODES = Object.keys(COUNTRY_NAMES_ES);
const COMMERCIAL_FIELDS = ["billing_type", "price_amount", "price_currency", "duration_amount", "duration_unit", "listings_mode", "listings_amount"];

const emptyForm = (companyLock) => ({
  name: "", description: "",
  status: "active", scope: companyLock ? "private" : "public",
  billing_type: "monthly", price_amount: "", price_currency: "USD",
  duration_amount: 1, duration_unit: "month",
  listings_mode: "custom", listings_amount: 5,
  renewal: "automatic", trial_enabled: false, trial_days: 14,
  countries: "all", benefits: [""],
  stripe_product_id: "", stripe_price_id: "", is_recurring: null,
  companies_using: [],
});

function Section({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 mb-5">
      <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, children, hint }) {
  return (
    <div className="mb-4">
      <label className="text-xs font-semibold text-slate-600 mb-1.5 block">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

const selectCls = "w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white";
const errMsg = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.message) return detail.message;
  return detail ? translateAdminError(detail) : fallback;
};

export default function AdminPlanDetailPage() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = !planId;
  const lockedCompanyId = searchParams.get("company_id");
  const lockedCompanyName = searchParams.get("company_name");
  const companyLock = lockedCompanyId ? { company_id: lockedCompanyId, company_name: lockedCompanyName } : null;

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => (isNew ? emptyForm(companyLock) : {}));
  const [companyQuery, setCompanyQuery] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(companyLock ? { company_id: companyLock.company_id, name: companyLock.company_name } : null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [stripeOpen, setStripeOpen] = useState(false);
  const [syncingStripe, setSyncingStripe] = useState(false);
  const [commercialStage, setCommercialStage] = useState(null); // null | "choose" | "confirmExisting"
  const [affectedCount, setAffectedCount] = useState(0);
  const [pendingPayload, setPendingPayload] = useState(null);

  const fetchPlan = useCallback(async () => {
    const res = await axios.get(`${API}/admin/plans/${planId}`, { headers: getAdminAuthHeaders() });
    const p = res.data;
    setForm({ ...p, benefits: p.benefits?.length ? p.benefits : [""] });
    if (p.scope === "private" && p.company_id) setSelectedCompany({ company_id: p.company_id, name: p.company_name });
  }, [planId]);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        await fetchPlan();
      } catch (err) {
        toast.error(errMsg(err, "No se encontró el plan"));
        navigate("/admin/plans");
      } finally {
        setLoading(false);
      }
    })();
  }, [isNew, fetchPlan, navigate]);

  const searchCompanies = useCallback(async (q) => {
    setCompanyQuery(q);
    if (!q || q.length < 2) { setCompanyResults([]); return; }
    try {
      const res = await axios.get(`${API}/admin/companies`, { headers: getAdminAuthHeaders(), params: { keyword: q, page: 1, limit: 8 } });
      setCompanyResults(res.data.companies || []);
    } catch { setCompanyResults([]); }
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const updateBenefit = (i, val) => { const list = [...form.benefits]; list[i] = val; set({ benefits: list }); };
  const addBenefit = () => set({ benefits: [...form.benefits, ""] });
  const removeBenefit = (i) => set({ benefits: form.benefits.filter((_, idx) => idx !== i) });

  const toggleCountriesAll = (all) => set({ countries: all ? "all" : [] });
  const toggleCountry = (code) => {
    const current = Array.isArray(form.countries) ? form.countries : [];
    set({ countries: current.includes(code) ? current.filter((c) => c !== code) : [...current, code] });
  };

  const validate = () => {
    if (!form.name.trim()) { toast.error("El nombre del plan es obligatorio"); return false; }
    if (form.scope === "private" && !selectedCompany) { toast.error("Elegí la empresa para este plan privado"); return false; }
    return true;
  };

  const buildPayload = () => ({
    name: form.name, description: form.description, status: form.status,
    billing_type: form.billing_type, price_amount: form.price_amount === "" ? null : Number(form.price_amount),
    price_currency: form.price_currency, duration_amount: form.duration_amount, duration_unit: form.duration_unit,
    listings_mode: form.listings_mode, listings_amount: form.listings_amount, renewal: form.renewal,
    trial_enabled: form.trial_enabled, trial_days: form.trial_days, countries: form.countries,
    benefits: form.benefits.map((b) => b.trim()).filter(Boolean),
    stripe_product_id: form.stripe_product_id, stripe_price_id: form.stripe_price_id,
    is_recurring: form.is_recurring,
  });

  const save = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      if (isNew) {
        const payload = {
          ...buildPayload(), scope: form.scope,
          company_id: form.scope === "private" ? selectedCompany.company_id : undefined,
          auto_assign: form.scope === "private",
        };
        const res = await axios.post(`${API}/admin/plans`, payload, { headers: getAdminAuthHeaders() });
        toast.success(`Plan "${form.name}" creado`);
        if (companyLock) navigate(`/admin/companies/${companyLock.company_id}?tab=access`);
        else navigate(`/admin/plans/${res.data.plan_id}`);
      } else {
        await submitEdit(buildPayload());
      }
    } catch (err) {
      toast.error(errMsg(err, "No se pudo guardar el plan"));
    } finally {
      setSaving(false);
    }
  };

  const submitEdit = async (payload) => {
    try {
      await axios.put(`${API}/admin/plans/${planId}`, payload, { headers: getAdminAuthHeaders() });
      toast.success(`Plan "${form.name}" actualizado`);
      setCommercialStage(null);
      setPendingPayload(null);
      await fetchPlan();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err.response?.status === 409 && detail?.requires_confirmation) {
        setAffectedCount(detail.affected_companies_count);
        setPendingPayload(payload);
        setCommercialStage("choose");
      } else {
        toast.error(errMsg(err, "No se pudo guardar el plan"));
      }
    }
  };

  const applyOnlyToNew = () => submitEdit({ ...pendingPayload, apply_to_existing: false });
  const applyToExisting = () => submitEdit({ ...pendingPayload, apply_to_existing: true });

  const duplicate = async () => {
    try {
      const res = await axios.post(`${API}/admin/plans/${planId}/duplicate`, {}, { headers: getAdminAuthHeaders() });
      toast.success(`"${form.name}" duplicado`);
      navigate(`/admin/plans/${res.data.plan_id}`);
    } catch (err) {
      toast.error(errMsg(err, "No se pudo duplicar el plan"));
    }
  };

  const toggleActive = async () => {
    try {
      await axios.post(`${API}/admin/plans/${planId}/${form.status === "active" ? "deactivate" : "activate"}`, {}, { headers: getAdminAuthHeaders() });
      toast.success(form.status === "active" ? "Plan desactivado" : "Plan activado");
      await fetchPlan();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo actualizar el estado del plan"));
    }
  };

  const confirmArchive = async () => {
    try {
      await axios.post(`${API}/admin/plans/${planId}/archive`, {}, { headers: getAdminAuthHeaders() });
      toast.success(`${form.name} fue archivado`);
      setArchiveOpen(false);
      navigate("/admin/plans");
    } catch (err) {
      toast.error(errMsg(err, "No se pudo archivar el plan"));
      setArchiveOpen(false);
    }
  };

  const syncStripe = async () => {
    setSyncingStripe(true);
    try {
      await axios.post(`${API}/admin/plans/${planId}/sync-stripe`, {}, { headers: getAdminAuthHeaders() });
      toast.success("Sincronizado con Stripe");
      await fetchPlan();
    } catch (err) {
      toast.error(errMsg(err, "No se pudo sincronizar con Stripe"));
    } finally {
      setSyncingStripe(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="admin-plan-detail-loading">
        <Loader2 className="h-6 w-6 text-[#028CF8] animate-spin" />
      </div>
    );
  }

  const showPrice = !["trial", "manual"].includes(form.billing_type);

  return (
    <div data-testid="admin-plan-detail-page" className="max-w-4xl">
      <button data-testid="admin-plan-back-button" onClick={() => navigate(companyLock ? `/admin/companies/${companyLock.company_id}?tab=access` : "/admin/plans")} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4">
        <ArrowLeft className="h-4 w-4" /> {companyLock ? "Volver a la empresa" : "Volver a Planes"}
      </button>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div className="min-w-0 flex-1">
          <Input
            data-testid="admin-plan-name-input"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
            placeholder="Nombre del plan (ej: Starter mensual)"
            className="text-xl font-bold h-12 mb-2"
          />
          <div className="flex items-center gap-2">
            {!isNew && <StatusBadge value={form.status} testId="admin-plan-detail-status" />}
            <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-full ${form.scope === "private" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-600"}`}>
              {form.scope === "private" ? <Lock className="h-3 w-3" /> : <Globe2 className="h-3 w-3" />} {form.scope === "private" ? "Privado" : "Público"}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && (
            <>
              <Button data-testid="admin-plan-duplicate-button" variant="outline" size="sm" onClick={duplicate}>
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Duplicar
              </Button>
              {form.status !== "archived" && (
                <Button data-testid="admin-plan-toggle-button" variant="outline" size="sm" onClick={toggleActive} className={form.status === "active" ? "text-amber-600 border-amber-200" : "text-emerald-600 border-emerald-200"}>
                  {form.status === "active" ? "Desactivar" : "Activar"}
                </Button>
              )}
              {form.status !== "archived" && (
                <Button data-testid="admin-plan-archive-button" variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setArchiveOpen(true)}>
                  <Archive className="h-3.5 w-3.5 mr-1.5" /> Archivar
                </Button>
              )}
            </>
          )}
          <Button data-testid="admin-plan-save-button" onClick={save} disabled={saving || form.status === "archived"} className="bg-[#028CF8] hover:bg-[#0270C5]">
            <Check className="h-3.5 w-3.5 mr-1.5" /> {saving ? "Guardando..." : "Guardar plan"}
          </Button>
        </div>
      </div>

      <Section title="Información general">
        <Field label="Descripción">
          <Textarea data-testid="admin-plan-description-input" value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="Breve descripción visible para el equipo" className="min-h-[70px]" />
        </Field>

        <Field label="Estado del plan">
          <div className="flex gap-2">
            <button data-testid="admin-plan-status-active" disabled={form.status === "archived"} onClick={() => set({ status: "active" })}
              className={`flex-1 h-10 rounded-lg text-sm font-semibold border disabled:opacity-40 ${form.status === "active" ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-200 text-slate-600 bg-white"}`}>
              Activo
            </button>
            <button data-testid="admin-plan-status-inactive" disabled={form.status === "archived"} onClick={() => set({ status: "inactive" })}
              className={`flex-1 h-10 rounded-lg text-sm font-semibold border disabled:opacity-40 ${form.status === "inactive" ? "bg-amber-500 text-white border-amber-500" : "border-slate-200 text-slate-600 bg-white"}`}>
              Inactivo
            </button>
          </div>
          {form.status === "archived" && <p className="text-xs text-slate-400 mt-1">Este plan está archivado. Usá "Duplicar" para crear una versión activa.</p>}
        </Field>

        <Field label="Alcance del plan">
          <div className="flex gap-2">
            <button data-testid="admin-plan-scope-public" disabled={!isNew || !!companyLock} onClick={() => set({ scope: "public" })}
              className={`flex-1 h-10 rounded-lg text-sm font-semibold border flex items-center justify-center gap-1.5 disabled:opacity-40 ${form.scope === "public" ? "bg-[#028CF8] text-white border-[#028CF8]" : "border-slate-200 text-slate-600 bg-white"}`}>
              <Globe2 className="h-4 w-4" /> Público (reutilizable)
            </button>
            <button data-testid="admin-plan-scope-private" disabled={!isNew || !!companyLock} onClick={() => set({ scope: "private" })}
              className={`flex-1 h-10 rounded-lg text-sm font-semibold border flex items-center justify-center gap-1.5 disabled:opacity-40 ${form.scope === "private" ? "bg-purple-600 text-white border-purple-600" : "border-slate-200 text-slate-600 bg-white"}`}>
              <Lock className="h-4 w-4" /> Privado (exclusivo de una empresa)
            </button>
          </div>
          {!isNew && <p className="text-xs text-slate-400 mt-1">El alcance no se puede cambiar después de creado el plan.</p>}
        </Field>

        {form.scope === "private" && (
          <Field label="Empresa asignada" hint={companyLock ? "Este plan se está creando desde la ficha de la empresa, no se puede cambiar." : (!isNew ? null : "Buscá por nombre de empresa")}>
            {companyLock || !isNew ? (
              <div data-testid="admin-plan-company-locked" className="h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center text-sm text-slate-700">
                <Users2 className="h-4 w-4 mr-2 text-slate-400" /> {selectedCompany?.name}
              </div>
            ) : selectedCompany ? (
              <div className="flex items-center gap-2">
                <div data-testid="admin-plan-company-selected" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center text-sm text-slate-700">
                  <Users2 className="h-4 w-4 mr-2 text-slate-400" /> {selectedCompany.name}
                </div>
                <Button data-testid="admin-plan-company-clear" variant="outline" size="sm" onClick={() => setSelectedCompany(null)}>Cambiar</Button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input data-testid="admin-plan-company-search" value={companyQuery} onChange={(e) => searchCompanies(e.target.value)} placeholder="Buscar empresa por nombre..." className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm" />
                {companyResults.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-y-auto" data-testid="admin-plan-company-results">
                    {companyResults.map((c) => (
                      <button key={c.company_id} data-testid={`admin-plan-company-option-${c.company_id}`} onClick={() => { setSelectedCompany({ company_id: c.company_id, name: c.name }); setCompanyResults([]); setCompanyQuery(""); }}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0">
                        {c.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Field>
        )}
      </Section>

      <Section title="Cobro y duración">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Tipo de cobro">
            <select data-testid="admin-plan-billing-type" value={form.billing_type} onChange={(e) => set({ billing_type: e.target.value })} className={selectCls}>
              {BILLING_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </Field>
          {form.billing_type === "custom" && (
            <Field label="Cobro en Stripe" hint="Los tipos mensual/anual/semanal/diario ya son suscripción y per_listing ya es pago único automáticamente — esto solo aplica a 'custom'.">
              <div className="flex gap-2">
                <button type="button" data-testid="admin-plan-recurring-subscription" onClick={() => set({ is_recurring: true })}
                  className={`flex-1 h-10 rounded-lg text-sm font-semibold border ${form.is_recurring === true ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600 bg-white"}`}>
                  Suscripción recurrente
                </button>
                <button type="button" data-testid="admin-plan-recurring-onetime" onClick={() => set({ is_recurring: false })}
                  className={`flex-1 h-10 rounded-lg text-sm font-semibold border ${form.is_recurring === false ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600 bg-white"}`}>
                  Pago único
                </button>
              </div>
            </Field>
          )}
          {showPrice ? (
            <Field label="Precio">
              <div className="flex gap-2">
                <select data-testid="admin-plan-currency" value={form.price_currency} onChange={(e) => set({ price_currency: e.target.value })} className={selectCls}>
                  {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
                </select>
                <Input data-testid="admin-plan-price-input" type="number" min="0" step="0.01" value={form.price_amount} onChange={(e) => set({ price_amount: e.target.value })} placeholder="0.00" className="h-10 w-24 shrink-0" />
              </div>
            </Field>
          ) : (
            <Field label="Precio">
              <div className="h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center text-sm text-slate-500">
                {form.billing_type === "trial" ? "Gratuito (trial)" : "Sin precio (acceso manual)"}
              </div>
            </Field>
          )}

          <Field label="Duración">
            <div className="flex gap-2">
              <select data-testid="admin-plan-duration-unit" value={form.duration_unit} onChange={(e) => set({ duration_unit: e.target.value })} className={selectCls}>
                {DURATION_UNITS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
              {form.duration_unit !== "unlimited" && (
                <Input data-testid="admin-plan-duration-amount" type="number" min="1" value={form.duration_amount} onChange={(e) => set({ duration_amount: Number(e.target.value) })} className="h-10 w-24 shrink-0" />
              )}
            </div>
          </Field>
          <Field label="Renovación">
            <select data-testid="admin-plan-renewal" value={form.renewal} onChange={(e) => set({ renewal: e.target.value })} className={selectCls}>
              {RENEWAL_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>

          <Field label="Avisos incluidos">
            <select data-testid="admin-plan-listings-mode" value={form.listings_mode} onChange={(e) => set({ listings_mode: e.target.value })} className={selectCls}>
              {LISTINGS_MODES.map((l) => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </Field>
          {form.listings_mode === "custom" && (
            <Field label="Cantidad de avisos">
              <Input data-testid="admin-plan-listings-amount" type="number" min="0" value={form.listings_amount ?? ""} onChange={(e) => set({ listings_amount: Number(e.target.value) })} className="h-10" />
            </Field>
          )}
        </div>

        <Field label="Trial dentro del plan" hint="Habilita días de prueba antes de cobrar por este plan (distinto al tipo de cobro 'Trial').">
          <div className="flex items-center gap-3">
            <button data-testid="admin-plan-trial-toggle" onClick={() => set({ trial_enabled: !form.trial_enabled })}
              className={`h-10 px-4 rounded-lg text-sm font-semibold border ${form.trial_enabled ? "bg-blue-600 text-white border-blue-600" : "border-slate-200 text-slate-600 bg-white"}`}>
              {form.trial_enabled ? "Trial habilitado" : "Sin trial"}
            </button>
            {form.trial_enabled && (
              <Input data-testid="admin-plan-trial-days" type="number" min="1" value={form.trial_days ?? ""} onChange={(e) => set({ trial_days: Number(e.target.value) })} placeholder="Días" className="h-10 w-28" />
            )}
          </div>
        </Field>
      </Section>

      <Section title="Países disponibles">
        <div className="flex items-center gap-2 mb-3">
          <button data-testid="admin-plan-countries-all" onClick={() => toggleCountriesAll(true)} className={`h-9 px-4 rounded-lg text-sm font-semibold border ${form.countries === "all" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 bg-white"}`}>Todos los países</button>
          <button data-testid="admin-plan-countries-specific" onClick={() => toggleCountriesAll(false)} className={`h-9 px-4 rounded-lg text-sm font-semibold border ${form.countries !== "all" ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 bg-white"}`}>Países específicos</button>
        </div>
        {form.countries !== "all" && (
          <div className="flex flex-wrap gap-2">
            {COUNTRY_CODES.map((code) => (
              <button key={code} data-testid={`admin-plan-country-${code}`} onClick={() => toggleCountry(code)}
                className={`h-9 px-3 rounded-lg text-sm border ${(form.countries || []).includes(code) ? "bg-[#028CF8] text-white border-[#028CF8]" : "border-slate-200 text-slate-600 bg-white"}`}>
                {COUNTRY_NAMES_ES[code]}
              </button>
            ))}
          </div>
        )}
      </Section>

      <Section title="Beneficios incluidos">
        <div className="space-y-2">
          {form.benefits.map((b, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input data-testid={`admin-plan-benefit-input-${i}`} value={b} onChange={(e) => updateBenefit(i, e.target.value)} placeholder="Ej: Soporte prioritario" className="h-10" />
              <button data-testid={`admin-plan-benefit-remove-${i}`} onClick={() => removeBenefit(i)} className="p-2 text-slate-400 hover:text-red-600"><XIcon className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
        <button data-testid="admin-plan-benefit-add" onClick={addBenefit} className="mt-2 flex items-center gap-1.5 text-xs font-semibold text-[#028CF8] hover:underline">
          <Plus className="h-3.5 w-3.5" /> Agregar beneficio
        </button>
      </Section>

      <div className="bg-white rounded-xl border border-slate-200 p-6 mb-5">
        <button data-testid="admin-plan-stripe-toggle" onClick={() => setStripeOpen((v) => !v)} className="w-full flex items-center justify-between text-left">
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-600">Integración Stripe</h3>
            <p className="text-xs text-slate-400 mt-1" data-testid="admin-plan-stripe-status">
              {form.stripe_price_id ? "Sincronizado con Stripe (modo test)" : "Pendiente — se sincroniza automáticamente al guardar un plan pago"}
            </p>
          </div>
          <span className="text-xs font-semibold text-[#028CF8] shrink-0">{stripeOpen ? "Ocultar" : "Mostrar"}</span>
        </button>
        {stripeOpen && (
          <div className="mt-4">
            <p className="text-xs text-slate-500 mb-4">Los IDs se crean y actualizan automáticamente en Stripe según el precio/moneda/duración configurados arriba — nunca se escriben a mano. Si cambiás precio, moneda o periodicidad, se crea un Price nuevo en Stripe y el anterior queda archivado (las empresas ya suscriptas con el precio viejo no se ven afectadas).</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Stripe Product ID">
                <div className="h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center text-sm text-slate-500 truncate" data-testid="admin-plan-stripe-product">{form.stripe_product_id || "—"}</div>
              </Field>
              <Field label="Stripe Price ID">
                <div className="h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center text-sm text-slate-500 truncate" data-testid="admin-plan-stripe-price">{form.stripe_price_id || "—"}</div>
              </Field>
            </div>
            {!isNew && (
              <Button data-testid="admin-plan-stripe-sync-button" variant="outline" size="sm" onClick={syncStripe} disabled={syncingStripe}>
                {syncingStripe && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />} Sincronizar con Stripe
              </Button>
            )}
          </div>
        )}
      </div>

      {!isNew && (
        <Section title={`Empresas usando este plan (${form.companies_using?.length || 0})`}>
          {(form.companies_using?.length || 0) === 0 ? (
            <p className="text-sm text-slate-500">Ninguna empresa está usando este plan actualmente.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {form.companies_using.map((c, i) => (
                <div key={i} data-testid={`admin-plan-company-usage-${i}`} className="flex items-center justify-between py-2.5">
                  <span className="text-sm text-slate-700 flex items-center gap-2"><Users2 className="h-3.5 w-3.5 text-slate-400" /> {c.name}</span>
                  {c.company_id && (
                    <button data-testid={`admin-plan-company-usage-view-${i}`} onClick={() => navigate(`/admin/companies/${c.company_id}`)} className="text-xs font-semibold text-[#028CF8] hover:underline">Ver empresa</button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      <AlertDialog open={commercialStage === "choose"} onOpenChange={(o) => !o && setCommercialStage(null)}>
        <AlertDialogContent data-testid="admin-plan-commercial-choose-dialog" className="max-w-[95vw] sm:max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cómo aplicar este cambio comercial?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-left">
                <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-sm">
                  Este plan ya está asignado a {affectedCount} empresa(s). Cambiaste precio, moneda, duración, avisos incluidos o tipo de cobro.
                </p>
                <p className="text-slate-600">Elegí si el cambio aplica solo a asignaciones nuevas, o también a las empresas que ya tienen este plan.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-2 sm:space-x-0">
            <AlertDialogCancel data-testid="admin-plan-commercial-choose-cancel" className="mt-0 shrink-0">Cancelar</AlertDialogCancel>
            <Button data-testid="admin-plan-commercial-choose-new-only" variant="outline" onClick={applyOnlyToNew} className="shrink-0 whitespace-normal text-center">Aplicar solo a nuevas asignaciones</Button>
            <Button data-testid="admin-plan-commercial-choose-existing" onClick={() => setCommercialStage("confirmExisting")} className="bg-amber-600 hover:bg-amber-700 text-white shrink-0 whitespace-normal text-center">
              Aplicar también a empresas existentes
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={commercialStage === "confirmExisting"} onOpenChange={(o) => !o && setCommercialStage("choose")}>
        <AlertDialogContent data-testid="admin-plan-commercial-confirm-existing-dialog" className="max-w-[95vw] sm:max-w-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Confirmás aplicar el cambio a {affectedCount} empresa(s)?</AlertDialogTitle>
            <AlertDialogDescription>
              Las condiciones comerciales actuales de esas empresas van a actualizarse con los nuevos valores. Esta acción queda registrada en auditoría.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-2 sm:space-x-0">
            <AlertDialogCancel data-testid="admin-plan-commercial-confirm-existing-cancel" className="mt-0 shrink-0">Volver</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-plan-commercial-confirm-existing-button" onClick={applyToExisting} className="bg-red-600 hover:bg-red-700 shrink-0">
              Sí, aplicar a todas
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={archiveOpen} onOpenChange={setArchiveOpen}>
        <AlertDialogContent data-testid="admin-plan-detail-archive-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Archivar "{form.name}"?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-left">
                {(form.companies_using?.length || 0) > 0 && (
                  <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-sm">
                    {form.companies_using.length} empresa(s) están usando este plan actualmente. Sus accesos activos NO se ven afectados, pero el plan ya no podrá asignarse a empresas nuevas.
                  </p>
                )}
                <p className="text-slate-600">Se conserva el plan y todo su historial de asignaciones — no se borra físicamente.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-plan-detail-archive-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="admin-plan-detail-archive-confirm-button" onClick={confirmArchive} className="bg-red-600 hover:bg-red-700">Archivar plan</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
